"use client"

import { User } from "lucide-react"
import Image from "next/image"
import React, { useState } from "react"

interface UserAvatarProps {
  userId?: string
  userName: string
  photoUrl?: string | null // Can be explicitly null to skip photo loading
  size?: "xs" | "sm" | "md" | "lg"
  className?: string
  priority?: boolean
  fallbackIcon?: React.ReactNode
}

/**
 * Safe UserAvatar component V2 that prevents 404 errors by:
 * 1. Not making any HEAD requests for validation
 * 2. Using Next.js Image with proper error handling
 * 3. Providing immediate fallback UI on error
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  userName,
  photoUrl,
  size = "md",
  className = "",
  priority = false,
  fallbackIcon,
}) => {
  const [imageError, setImageError] = useState(false)

  // Size configurations
  const sizeMap = {
    xs: {
      iconSize: "h-4 w-4",
      dimensions: "h-6 w-6",
      pixelSize: 24,
    },
    sm: {
      iconSize: "h-6 w-6",
      dimensions: "h-8 w-8",
      pixelSize: 32,
    },
    md: {
      iconSize: "h-8 w-8",
      dimensions: "h-12 w-12",
      pixelSize: 48,
    },
    lg: {
      iconSize: "h-10 w-10",
      dimensions: "h-14 w-14",
      pixelSize: 56,
    },
  }

  const { iconSize, dimensions, pixelSize } = sizeMap[size]

  // Default fallback
  const defaultFallback = (
    <div
      className={`flex items-center justify-center rounded-full bg-foreground/10 ${dimensions} ${className}`}
    >
      {fallbackIcon || <User className={`${iconSize} opacity-20`} />}
    </div>
  )

  // If photoUrl is explicitly null, don't attempt to load any photo
  if (photoUrl === null) {
    return defaultFallback
  }

  // If image had an error, show fallback
  if (imageError) {
    return defaultFallback
  }

  // If we have an explicit photoUrl (base64 or regular URL), use it directly
  if (photoUrl) {
    return (
      <div
        className={`${dimensions} ${className} overflow-hidden rounded-full`}
      >
        <Image
          src={photoUrl}
          alt={userName}
          width={pixelSize}
          height={pixelSize}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
          priority={priority}
          loading={priority ? "eager" : "lazy"}
          sizes={`${pixelSize}px`}
        />
      </div>
    )
  }

  // If we have a userId but no explicit photoUrl, try Graph API directly
  // Only try Graph API if we have a valid userId (not empty string)
  if (userId && userId.trim() !== "") {
    const graphPhotoUrl = `/api/graph-photo/${userId}/96x96`

    return (
      <div
        className={`${dimensions} ${className} overflow-hidden rounded-full`}
      >
        <Image
          src={graphPhotoUrl}
          alt={userName}
          width={pixelSize}
          height={pixelSize}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
          priority={priority}
          loading={priority ? "eager" : "lazy"}
          sizes={`${pixelSize}px`}
        />
      </div>
    )
  }

  // Default case - show fallback
  return defaultFallback
}

export default UserAvatar
